import { useEffect, useState } from "react";
import { Upload, Sparkles, ImagePlus, Loader2, Check, Mail, Clock, PenTool, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const isHeic = (f) => /\.(heic|heif)$/i.test(f.name) || /heic|heif/i.test(f.type);
const readAsDataURL = (blob) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = () => rej(r.error);
  r.readAsDataURL(blob);
});

const toUploadPayload = async (f) => {
  try {
    const bmp = await createImageBitmap(f);
    const scale = Math.min(1, 2560 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const out = c.toDataURL("image/jpeg", 0.9);
    if (out.length > 100) return out;
  } catch { /* browser can't decode (e.g. HEIC on Chrome) — send original */ }
  return readAsDataURL(f);
};

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${x.loc?.slice(-1)[0] || "field"}: ${x.msg}`).join(", ");
  return e?.response ? fallback : "Network error — please check your connection and try again.";
};

const EMPTY = { name: "", email: "", clinic: "", phone: "", notes: "" };

export default function ClinicRenderStudio({ products }) {
  const [slug, setSlug] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { if (!slug && products.length) setSlug(products[0].slug); }, [products, slug]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/") && !isHeic(f)) return toast.error("Please choose an image file");
    if (f.size > 15 * 1024 * 1024) return toast.error("Image must be under 15MB");
    setFile(f);
    setPreview(isHeic(f) ? "" : URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please upload a photo of your facility");
    setSubmitting(true);
    try {
      const image_base64 = await toUploadPayload(file);
      const { data } = await api.post("/render-requests", {
        ...form, image_base64, filename: file.name || "facility.jpg", product_slug: slug,
      });
      setDone({ ...data, email: form.email });
    } catch (err) {
      toast.error(errMsg(err, "Submission failed — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setDone(null); setFile(null); setPreview(""); setForm(EMPTY); };

  return (
    <section id="visualize" data-testid="render-request-section" className="relative bg-[#1C1C1C] text-[#FAF9F6] py-24 lg:py-32 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#C9A961]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <StudioHeader />
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mt-16">
          <div className="lg:col-span-7">
            {done ? <Success done={done} onReset={reset} /> : (
              <form onSubmit={submit} data-testid="render-request-form" className="space-y-10">
                <ChamberPicker products={products} slug={slug} setSlug={setSlug} disabled={submitting} />
                <Dropzone preview={preview} file={file} onFile={pickFile} disabled={submitting} />
                <div>
                  <StepLabel n="03" text="Where should we send it?" />
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 mt-4">
                    <Field label="Full name *" testid="render-name-input" value={form.name} onChange={set("name")} required disabled={submitting} placeholder="Jane Doe" />
                    <Field label="Email *" testid="render-email-input" type="email" value={form.email} onChange={set("email")} required disabled={submitting} placeholder="you@yourmedspa.com" />
                    <Field label="Clinic name" testid="render-clinic-input" value={form.clinic} onChange={set("clinic")} disabled={submitting} placeholder="Optional" />
                    <Field label="Phone" testid="render-phone-input" type="tel" value={form.phone} onChange={set("phone")} disabled={submitting} placeholder="Optional" />
                  </div>
                  <div className="mt-4">
                    <Field label="Placement notes" testid="render-notes-input" value={form.notes} onChange={set("notes")} disabled={submitting}
                      placeholder="e.g. against the back wall, left of the window" />
                  </div>
                </div>
                <button data-testid="render-submit-btn" type="submit" disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#C9A961] text-[#0A0A0A] px-8 py-5 font-medium tracking-wide hover:bg-[#FAF9F6] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                  {submitting ? "Sending your request…" : "Request my free render"}
                </button>
              </form>
            )}
          </div>
          <HowItWorks />
        </div>
      </div>
    </section>
  );
}

function StudioHeader() {
  return (
    <div className="grid lg:grid-cols-12 gap-8 items-end">
      <div className="lg:col-span-7">
        <div className="overline text-[#C9A961]">Complimentary Suite Render</div>
        <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
          See the chamber <em className="italic text-[#C9A961]">in your space</em> — before it ships.
        </h2>
      </div>
      <p className="lg:col-span-5 text-sm md:text-base text-[#D8D3C7] leading-relaxed">
        Send us a photo of your treatment room and choose a chamber. Our design team will create a custom
        photoreal render of the TheraTec unit in your facility and email it to you within 24 hours.
      </p>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { icon: ImagePlus, title: "Upload your space", body: "A wide, well-lit photo of the room where the chamber would live." },
    { icon: PenTool, title: "Our designers render it", body: "Hand-finished by the TheraTec team — true-to-scale, matched to your lighting." },
    { icon: Mail, title: "Delivered to your inbox", body: "Your custom render arrives by email within 24 hours, with a floor-plan consult offer." },
  ];
  return (
    <aside data-testid="render-how-it-works" className="lg:col-span-5 lg:sticky lg:top-28 self-start border border-[#FAF9F6]/10 p-8 lg:p-10">
      <div className="overline text-[#C9A961]">How it works</div>
      <ol className="mt-8 space-y-8">
        {steps.map((s, i) => (
          <li key={s.title} className="flex gap-5">
            <span className="shrink-0 w-11 h-11 border border-[#C9A961]/50 flex items-center justify-center text-[#C9A961]">
              <s.icon size={18} strokeWidth={1.5} />
            </span>
            <div>
              <div className="font-serif-editorial text-xl">{`0${i + 1}`} · {s.title}</div>
              <p className="mt-2 text-sm text-[#D8D3C7]/80 leading-relaxed">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-10 pt-6 border-t border-[#FAF9F6]/10 flex items-center gap-3 text-xs text-[#FAF9F6]/60">
        <Clock size={14} className="text-[#C9A961]" /> Free, no obligation · 24-hour turnaround
      </div>
    </aside>
  );
}

function Success({ done, onReset }) {
  return (
    <div data-testid="render-request-success" className="border border-[#C9A961]/40 p-10 lg:p-14" style={{ animation: "tt-fade-up 700ms ease both" }}>
      <span className="w-14 h-14 rounded-full bg-[#C9A961] text-[#0A0A0A] flex items-center justify-center"><Check size={24} /></span>
      <h3 className="font-serif-editorial text-3xl lg:text-4xl mt-8 leading-tight">Request received.</h3>
      <p className="mt-4 text-[#D8D3C7] leading-relaxed max-w-lg">
        Our design team is creating your custom <span className="text-[#C9A961]">{done.product_name}</span> render.
        We'll email it to <span data-testid="render-success-email" className="text-[#FAF9F6]">{done.email}</span> within 24 hours.
      </p>
      <button data-testid="render-request-another-btn" onClick={onReset}
        className="mt-10 inline-flex items-center gap-2 border border-[#FAF9F6]/30 px-6 py-3 text-sm tracking-wide hover:bg-[#FAF9F6] hover:text-[#0A0A0A] transition-colors duration-300">
        <RotateCcw size={15} /> Request another render
      </button>
    </div>
  );
}

function ChamberPicker({ products, slug, setSlug, disabled }) {
  return (
    <div>
      <StepLabel n="01" text="Choose your chamber" />
      <div className="grid grid-cols-3 gap-3 mt-4">
        {products.map((p) => (
          <button type="button" key={p.slug} data-testid={`render-chamber-${p.slug}`} disabled={disabled} onClick={() => setSlug(p.slug)}
            className={`group text-left border transition-colors duration-300 ${slug === p.slug ? "border-[#C9A961]" : "border-[#FAF9F6]/15 hover:border-[#FAF9F6]/40"}`}>
            <div className="aspect-square overflow-hidden bg-[#FAF9F6]">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <span className="text-xs leading-tight">{p.name.replace("TheraTec ", "")}</span>
              {slug === p.slug && <Check size={14} className="text-[#C9A961] shrink-0" />}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Dropzone({ preview, file, onFile, disabled }) {
  const [over, setOver] = useState(false);
  return (
    <div>
      <StepLabel n="02" text="Upload a photo of your facility" />
      <label data-testid="render-dropzone"
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); !disabled && onFile(e.dataTransfer.files[0]); }}
        className={`mt-4 relative flex flex-col items-center justify-center aspect-[16/9] border border-dashed cursor-pointer overflow-hidden transition-colors duration-300 ${over ? "border-[#C9A961] bg-[#C9A961]/5" : "border-[#FAF9F6]/25 hover:border-[#C9A961]/70"}`}>
        <input data-testid="render-file-input" type="file" accept="image/*,.heic,.heif" className="hidden" disabled={disabled}
          onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} />
        {file && !preview ? (
          <>
            <ImagePlus size={28} strokeWidth={1.25} className="text-[#C9A961]" />
            <span data-testid="render-file-name" className="mt-4 text-sm">{file.name}</span>
            <span className="mt-1 text-xs text-[#FAF9F6]/50">Photo attached · click to replace</span>
          </>
        ) : preview ? (
          <>
            <img data-testid="render-file-preview" src={preview} alt="Your facility" className="absolute inset-0 w-full h-full object-cover" />
            <span className="relative mt-auto mb-3 inline-flex items-center gap-2 bg-[#0A0A0A]/70 backdrop-blur px-3 py-1.5 text-xs">
              <ImagePlus size={14} /> Replace photo
            </span>
          </>
        ) : (
          <>
            <Upload size={28} strokeWidth={1.25} className="text-[#C9A961]" />
            <span className="mt-4 text-sm">Drop a photo or <span className="text-[#C9A961] underline underline-offset-4">browse</span></span>
            <span className="mt-1 text-xs text-[#FAF9F6]/50">Wide shot of the room · JPG, PNG, WEBP, HEIC · up to 15MB</span>
          </>
        )}
      </label>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder, disabled, required }) {
  return (
    <label className="block">
      <span className="overline text-[#FAF9F6]/60">{label}</span>
      <input data-testid={testid} type={type} value={value} disabled={disabled} placeholder={placeholder} required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border-b border-[#FAF9F6]/20 focus:border-[#C9A961] outline-none py-3 text-sm placeholder:text-[#FAF9F6]/30 transition-colors" />
    </label>
  );
}

function StepLabel({ n, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-serif-editorial text-[#C9A961] text-lg">{n}</span>
      <span className="h-px w-6 bg-[#C9A961]/50" />
      <span className="overline text-[#FAF9F6]/80">{text}</span>
    </div>
  );
}
